import FullPageLoading from "components/FullPageLoading"
import { usePgCTLOfferLetter } from "features/Home/OfferLetter/PgCTL/usePgCTLOfferLetter"
import SuraasaPlusNewYearOnboarding from "features/Home/SuraasaPlusNewYearOnboarding"
import { SuraasaPlusNewYearProvider } from "features/Home/SuraasaPlusNewYearOnboarding/Context"
import PgCTLEnrollmentAgreement from "features/PgCTLEnrollmentAgreement"
import PgCTLOrientationVideo from "features/PgCTLOrientationVideo"
import SignUpOnboarding from "features/SignupOnboarding"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { context } from "global/Context/context"
import { useFeatureToggle } from "global/FeatureToggleProvider"
import { useContext, useEffect, useState } from "react"
import {
  Outlet,
  useLocation,
  useMatch,
  useSearchParams,
} from "react-router-dom"
import routes from "routes"
import {
  BROWSER_STORAGE_KEYS,
  PAYMENT_UNDER_PROCESS_PAGE_PORTAL,
} from "utils/constants"
import { isShadow } from "utils/shadow"
import { UserPreferenceManager } from "utils/userPreferenceManager"

const useIsPaymentPage = () => {
  const isPgctlPayment = Boolean(useMatch(routes.pgctlOnboarding.payment))
  const isGenericPayment = Boolean(useMatch(routes.payment))
  const isSubscription = Boolean(useMatch(routes.subscription))
  const isRetryPayment = Boolean(useMatch(routes.retryPayment))
  const isEmbeddedPayment = Boolean(useMatch(routes.embeddedPayment))

  return (
    isPgctlPayment ||
    isGenericPayment ||
    isSubscription ||
    isRetryPayment ||
    isEmbeddedPayment
  )
}

const Authentication = () => {
  const { isAuthenticated, isGrowthSpaceUser } = useContext(context)
  const { subscriptionDetailsQuery, isSPNYUser } = useSuraasaPlusContext()
  const {
    query: offerLetterQuery,
    offerLetter,
    isRegistrationDone,
  } = usePgCTLOfferLetter()

  const featureToggles = useFeatureToggle()

  const [userPreferencesLoading, setUserPreferencesLoading] = useState(true)

  const hasOfferLetterAndNotSeenIt =
    offerLetterQuery.data?.product &&
    !UserPreferenceManager.get(BROWSER_STORAGE_KEYS.pgctlOfferLetterViewedAt)

  const isOfferLetterRegistrationPending =
    Boolean(offerLetter) && !isRegistrationDone

  useEffect(() => {
    // Set onboarding mode to optional if user has an offer letter and hasn't seen it yet,
    // or if their offer letter registration isn't done yet.
    // Because we want to show offer letter first before onboarding. They can complete onboarding later on their own time.
    // Wait for userPreferencesLoading so this never writes before UserPreferenceManager.init() has loaded/migrated preferences.
    if (
      !userPreferencesLoading &&
      (hasOfferLetterAndNotSeenIt || isOfferLetterRegistrationPending)
    ) {
      UserPreferenceManager.set(
        BROWSER_STORAGE_KEYS.signupOnboardingPromptMode,
        "optional"
      )
    }
  }, [
    hasOfferLetterAndNotSeenIt,
    isOfferLetterRegistrationPending,
    isAuthenticated,
    userPreferencesLoading,
  ])

  const { pathname } = useLocation()

  const isPaymentPage = useIsPaymentPage()

  const isCPAT = pathname.includes("/cpat")
  const [searchParams] = useSearchParams()

  const temp_skipSignupOnboarding = searchParams.get("form_so") === "0"

  useEffect(() => {
    const getPreferences = async () => {
      setUserPreferencesLoading(true)
      try {
        await UserPreferenceManager.init()
      } catch (e) {
        console.error("> Failed to initialize user preferences", e)
      } finally {
        setUserPreferencesLoading(false)
      }
    }

    getPreferences()
  }, [isAuthenticated])

  const signupOnboardingCompleted =
    UserPreferenceManager.get(
      BROWSER_STORAGE_KEYS.signupOnboardingCompleted
    ) === "true"
  const forceSignupOnboarding =
    UserPreferenceManager.get(
      BROWSER_STORAGE_KEYS.signupOnboardingPromptMode
    ) !== "optional"

  if (
    !isAuthenticated ||
    userPreferencesLoading ||
    offerLetterQuery.isLoading ||
    /* Show loading when subscription details are fetched for the first time when the platform loads */
    /* We are not using isLoading here because we later need to refetch this query in payment flows and we don't want to trigger a global loading in that case. */
    !subscriptionDetailsQuery?.isFetched
  )
    return <FullPageLoading debugMessage="From Authentication/index.tsx" />

  if (
    !isCPAT &&
    !isPaymentPage &&
    !temp_skipSignupOnboarding &&
    featureToggles.platformOnboarding.isEnabled &&
    isAuthenticated &&
    !isShadow() &&
    !userPreferencesLoading &&
    forceSignupOnboarding &&
    !signupOnboardingCompleted &&
    // !IS_DEV_ENVIRONMENT &&
    // !IS_LOCALHOST &&
    !isGrowthSpaceUser &&
    !hasOfferLetterAndNotSeenIt &&
    !isOfferLetterRegistrationPending &&
    !isSPNYUser
  ) {
    return <SignUpOnboarding />
  }

  return (
    <>
      <div id={PAYMENT_UNDER_PROCESS_PAGE_PORTAL} />
      <PgCTLEnrollmentAgreement>
        <PgCTLOrientationVideo>
          <SuraasaPlusNewYearProvider>
            <Outlet />
            <SuraasaPlusNewYearOnboarding />
          </SuraasaPlusNewYearProvider>
        </PgCTLOrientationVideo>
      </PgCTLEnrollmentAgreement>
    </>
  )
}

export default Authentication
